import { useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";
import { useContext, useEffect } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import { entityKeys } from "../../../../api/queryKeys";
import { EnvFeatureFlagContext } from "../../../../contexts/EnvFeaturesContext";
import { extractTypeFromEntityId } from "../../../posture/controls/components/EntitiesSection/utils";

export const useEntities = () => {
  const { isActiveRds } = useContext(EnvFeatureFlagContext);
  const {
    fetchEntitiesByType,
    fetchEntitiesByTypeRds,
    fetchAllEntities,
    fetchAllEntitiesRds,
    fetchEntitiesCount,
    fetchEntitiesCountRds,
  } = useAPI();

  const ENTITY_TYPE_PAGE_SIZE = 10000;
  const ALL_ENTITIES_PAGE_SIZE = 30000;
  const flattenEntities = (entities) => {
    if (!Array.isArray(entities)) {
      return [];
    }

    return entities.map(({ entity_data, ...entity }) => ({
      integrations: entity.sources,
      id: entity.unique_id,
      ...entity,
      ...entity_data,
    }));
  };

  const flattenEntity = (entity) => {
    if (!entity) return null;
    return flattenEntities([entity])[0];
  };

  const fetchFlattenEntitiesByType = async ({ queryKey }) => {
    try {
      const [, type] = queryKey;
      if (!type) {
        return [];
      }

      let allEntities = [];
      let currentPage = 1;
      let hasMore = true;
      const pageSize = ENTITY_TYPE_PAGE_SIZE;

      while (hasMore) {
        const response = (
          await (isActiveRds ? fetchEntitiesByTypeRds : fetchEntitiesByType)(
            type,
            currentPage,
            pageSize
          )
        ).data;
        const entities = response.entities || [];
        allEntities = [...allEntities, ...flattenEntities(entities)];

        hasMore = response.pagination?.has_next || false;
        currentPage += 1;
      }

      return allEntities;
    } catch (err) {
      return [];
    }
  };

  const fetchAllEntitiesParallel = async (
    pageSize = ALL_ENTITIES_PAGE_SIZE
  ) => {
    const response = await (isActiveRds
      ? fetchEntitiesCountRds
      : fetchEntitiesCount)();
    const totalCount = response.data;
    const pagesCount = Math.ceil(totalCount / pageSize);
    const pageNumbers = Array.from({ length: pagesCount }, (_, idx) => idx + 1);
    const pages = await Promise.all(
      pageNumbers.map(async (pageNumber) => {
        const response = (
          await (isActiveRds ? fetchAllEntitiesRds : fetchAllEntities)(
            pageNumber,
            pageSize
          )
        ).data;
        return flattenEntities(response.entities || []);
      })
    );
    return pages.flat();
  };

  const fetchFlattenEntitiesByTypePaginated = async ({
    queryKey,
    pageParam = 1,
  }) => {
    try {
      const [, type, pageSize] = queryKey;
      if (!type) {
        return { entities: [], pagination: {} };
      }
      const response = (
        await (isActiveRds ? fetchEntitiesByTypeRds : fetchEntitiesByType)(
          type,
          pageParam,
          pageSize
        )
      ).data;
      return {
        entities: flattenEntities(response.entities || []),
        pagination: response.pagination || {},
      };
    } catch (err) {
      return { entities: [], pagination: {} };
    }
  };

  const fetchAllEntitiesByType = async (
    type,
    pageSize = ENTITY_TYPE_PAGE_SIZE
  ) => {
    try {
      if (!type) {
        return [];
      }

      let allEntities = [];
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        const response = (
          await (isActiveRds ? fetchEntitiesByTypeRds : fetchEntitiesByType)(
            type,
            currentPage,
            pageSize
          )
        ).data;
        const entities = response.entities || [];
        allEntities = [...allEntities, ...flattenEntities(entities)];

        hasMore = response.pagination?.has_next || false;
        currentPage += 1;
      }

      return allEntities;
    } catch (err) {
      return [];
    }
  };

  const useQueryForEntitiesByType = ({ type, ...props }) =>
    useQuery({
      queryKey: entityKeys.entitiesByType(type),
      queryFn: fetchFlattenEntitiesByType,

      ...props,
    });

  const useInfiniteQueryForEntitiesByType = ({
    type,
    pageSize = ENTITY_TYPE_PAGE_SIZE,
    ...props
  }) =>
    useInfiniteQuery({
      queryKey: entityKeys.entitiesByTypePaginated(type, pageSize),
      queryFn: fetchFlattenEntitiesByTypePaginated,

      getNextPageParam: (lastPage) => {
        if (lastPage.pagination.has_next) {
          return lastPage.pagination.page + 1;
        }
        return undefined;
      },
      getPreviousPageParam: (firstPage) => {
        if (firstPage.pagination.has_previous) {
          return firstPage.pagination.page - 1;
        }
        return undefined;
      },
      initialPageParam: 1,
      ...props,
    });

  const useInfiniteQueryForEntitiesByTypeAutoFetch = ({
    type,
    pageSize = ENTITY_TYPE_PAGE_SIZE,
    ...props
  }) => {
    const query = useInfiniteQuery({
      queryKey: entityKeys.entitiesByTypePaginated(type, pageSize),
      queryFn: fetchFlattenEntitiesByTypePaginated,

      getNextPageParam: (lastPage) => {
        if (lastPage.pagination.has_next) {
          return lastPage.pagination.page + 1;
        }
        return undefined;
      },
      getPreviousPageParam: (firstPage) => {
        if (firstPage.pagination.has_previous) {
          return firstPage.pagination.page - 1;
        }
        return undefined;
      },
      initialPageParam: 1,
      ...props,
    });

    useEffect(() => {
      if (query.hasNextPage && !query.isFetchingNextPage && !query.isFetching) {
        query.fetchNextPage();
      }
    }, [
      query,
      query.hasNextPage,
      query.isFetchingNextPage,
      query.isFetching,
      query.data?.pages?.length,
    ]);

    const allEntities =
      query.data?.pages.flatMap((page) => page.entities) || [];
    const totalCount = query.data?.pages?.[0]?.pagination?.total_count || 0;
    const isLoading = query.status === "pending";
    const isLoadingAll = query.hasNextPage || query.isFetchingNextPage;

    return {
      ...query,
      entities: allEntities,
      totalCount,
      isLoadingAll,
      isLoading,
      isFullyLoaded: !query.hasNextPage && query.isSuccess,
    };
  };

  const useQueryForAllEntities = (enabled) =>
    useQuery({
      queryKey: entityKeys.allEntities(),
      queryFn: () => fetchAllEntitiesParallel(ALL_ENTITIES_PAGE_SIZE),

      enabled,
    });

  const useInfiniteQueriesByTypes = ({
    types,
    pageSize = ENTITY_TYPE_PAGE_SIZE,
    ...props
  }) => {
    const entityQueries = useQueries({
      queries: types.map((type) => ({
        queryKey: entityKeys.entitiesByType(type),
        queryFn: () => fetchAllEntitiesByType(type, pageSize),

        ...props,
      })),
    });

    const entitiesAreLoading = entityQueries.some((query) => query.isLoading);
    const entitiesAreFetching = entityQueries.some((query) => query.isFetching);

    const entitiesAreFetchingByType = entityQueries.reduce(
      (acc, query, idx) => {
        if (types[idx]) {
          acc[types[idx]] = query.isFetching || false;
        }
        return acc;
      },
      {}
    );
    const entitiesAreLoadingByType = entityQueries.reduce((acc, query, idx) => {
      if (types[idx]) {
        acc[types[idx]] = query.isLoading || false;
      }
      return acc;
    }, {});

    const entitiesQueriesAreSuccess = entityQueries.every(
      (query) => query.isSuccess
    );
    const isFetchingNextPage = false;
    const hasNextPage = false;

    const entities = entityQueries?.some((query) => !query?.isSuccess)
      ? null
      : entityQueries?.flatMap((query) => query.data || []);

    const fetchNextPageForAll = () => {};

    const totalCounts = entityQueries.reduce((acc, query, idx) => {
      const totalCount = query.data?.length || 0;
      if (types[idx]) {
        acc[types[idx]] = totalCount;
      }
      return acc;
    }, {});

    return {
      entityQueries,
      entitiesAreLoading,
      entitiesAreFetching,
      entitiesAreFetchingByType,
      entitiesAreLoadingByType,
      entitiesQueriesAreSuccess,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPageForAll,
      entities,
      totalCounts,
    };
  };

  const useInfiniteQueriesByTypesAutoFetch = ({
    types,
    pageSize = ENTITY_TYPE_PAGE_SIZE,
    ...props
  }) => {
    const entityQueries = useQueries({
      queries: types.map((type) => ({
        queryKey: entityKeys.entitiesByType(type),
        queryFn: () => fetchAllEntitiesByType(type, pageSize),

        ...props,
      })),
    });

    const entitiesAreLoading = entityQueries.some((query) => query.isLoading);
    const entitiesQueriesAreSuccess = entityQueries.every(
      (query) => query.isSuccess
    );
    const isLoadingAll = entitiesAreLoading;
    const isFullyLoaded = entitiesQueriesAreSuccess;

    const entities = entityQueries?.some((query) => !query?.isSuccess)
      ? null
      : entityQueries?.flatMap((query) => query.data || []);

    const totalCounts = entityQueries.reduce((acc, query, idx) => {
      const totalCount = query.data?.length || 0;
      acc[types[idx]] = totalCount;
      return acc;
    }, {});

    return {
      entityQueries,
      entitiesAreLoading,
      entitiesQueriesAreSuccess,
      isLoadingAll,
      isFullyLoaded,
      entities,
      totalCounts,
    };
  };

  const useQueriesByTypes = ({ types, ...props }) => {
    /*
    This hook handles multiple queries for multiple entity types, and returns basic information like loading state and fetched entities.
    */
    const entityQueries = useQueries({
      queries: types.map((type) => ({
        queryKey: entityKeys.entitiesByType(type),
        queryFn: fetchFlattenEntitiesByType,

        ...props,
      })),
    });
    const entitiesAreLoading = entityQueries.some((query) => query.isLoading);

    const entitiesQueriesAreSuccess = entityQueries.every(
      (query) => query.isSuccess
    );

    const entities = entityQueries.some((query) => !query.isSuccess)
      ? null
      : entityQueries?.flatMap((query) => query.data);

    return {
      entityQueries,
      entitiesAreLoading,
      entitiesQueriesAreSuccess,
      entities,
    };
  };

  const useQueriesForEntities = ({ entityIDs, ...props }) => {
    /*
    This hook handles multiple queries for multiple entities (potentially from different types), and returns basic information like loading state and fetched entities.
    */
    const entityTypes = Array.from(
      new Set(entityIDs.map((entityID) => extractTypeFromEntityId(entityID)))
    );
    const entityQueries = useQueries({
      queries: entityTypes.map((type) => ({
        queryKey: entityKeys.entitiesByType(type),
        queryFn: fetchFlattenEntitiesByType,

        ...props,
      })),
    });

    const entitiesAreLoading = entityQueries.some((query) => query.isLoading);

    const allFetchedEntities = entityQueries.some((query) => !query.isSuccess)
      ? null
      : entityQueries?.flatMap((query) => query.data);

    const requestedEntities =
      allFetchedEntities === null
        ? null
        : allFetchedEntities.filter((entity) =>
            entityIDs.includes(entity.entity_id)
          );

    return {
      entityQueries,
      entitiesAreLoading,
      allFetchedEntities,
      requestedEntities,
    };
  };

  return {
    // fetchFlattenEntitiesByType,
    flattenEntities,
    // fetchEntitiesByType,
    fetchEntitiesByTypeRds,
    useQueryForEntitiesByType,
    // useInfiniteQueryForEntitiesByType,
    // useInfiniteQueryForEntitiesByTypeAutoFetch,
    useInfiniteQueriesByTypes,
    // useInfiniteQueriesByTypesAutoFetch,
    useQueriesByTypes,
    useQueriesForEntities,
    flattenEntity,
    useQueryForAllEntities,
  };
};
